import React, { useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, Delete, Edit } from "@mui/icons-material";
import { AnalysisService } from "../../services/analysis/AnalysisService";
import { InspectionReviewService, ReviewedDetectionDto } from "../../services/ocr/InspectionReviewService";
import { videoPlayerService } from "../../services/video-player/VideoPlayerService";
import { translationService } from "../../services/translation/TranslationService";
import DetectionEditDialog, { EditableDetection } from "./DetectionEditDialog";
import DetectionCard from "./DetectionCard";
import { observationTextFromFields } from "./analysisObservationText";
import { formatDetectionTime } from "../../services/ocr/InspectionInfoStorageModel";
import {
  OcrCodedField,
  changedCodedFields,
  codedByKey,
  codedFromReviewDto,
  machineCounterpart,
} from "./detectionProvenance";
import { ApprovedList } from "./useApprovedList";
import SaveListButton from "./SaveListButton";
import ImageSegmentationOverlay from "./ImageSegmentationOverlay";
import { SegmentationService } from "../../services/segmentation/SegmentationService";
import { Segmentation } from "../../api-models/response/VideoAnalysisDataResponse";
import { secondsToOcrTimestamp, ocrTsToSeconds } from "./playbackSync";
import {
  DetectionSource,
  analysisToReviewedDetections,
  buildAnalysisDetections,
  interpolatedDistanceAtTime,
  distanceTrack,
  provenanceOf,
} from "./analysisDetections";
import {
  DEFAULT_DETECTION_STATUS,
  conditionColor,
  riskColor,
  statusColor,
} from "./detectionAssessment";
import { isDataUrl } from "./detectionFrames";

const t = (key: string) => translationService.translate(key);

interface VaDetectionListProps {
  review: InspectionReviewService;
  videoAnalysis: AnalysisService;
  /** The file whose video a frame is grabbed from when a saved row has none. */
  videoId?: number;
  /**
   * Which list is on screen. Both are EDITABLE: an edit made while looking at the analysis
   * output is written to the approved list, never back into the analysis output itself.
   */
  source?: DetectionSource;
  /** The approved list for the analysis side, and the only way to change it. */
  approved: ApprovedList;
  /**
   * "Mark damage": the segmentation service and the confidence-filtered masks to draw over each
   * card's frame. Same props DetectionList takes, so both tabs honour the one toggle.
   */
  segmentationEnabled?: boolean;
  segmentation?: SegmentationService;
  filteredSegmentations?: Segmentation[];
}

/**
 * The reviewer's working list of VIDEO-ANALYSIS detections.
 *
 * The Video Analysis tab previously had no editable list at all — only the pipe graph — so
 * an analysis-only inspection could not be corrected or given frames. This is the equivalent
 * of the Inspection tab's DetectionList, but bound to the review rather than to OCR output,
 * because video analysis produces no OCR detections to edit.
 */
const VaDetectionList: React.FC<VaDetectionListProps> = observer(
  ({
    review,
    videoAnalysis,
    videoId,
    source = "approved",
    approved,
    segmentationEnabled,
    segmentation,
    filteredSegmentations,
  }) => {
    const showSegmentation = !!segmentationEnabled && !!segmentation?.segmentationSuccessful;
    const [editIndex, setEditIndex] = useState<number | null>(null);
    const [addOpen, setAddOpen] = useState(false);
    const [addInitial, setAddInitial] = useState<Partial<EditableDetection> | null>(null);
    // The video position the frame now sitting in the Add dialog was grabbed at. Kept so a
    // reviewer who then types a DIFFERENT time does not walk away with a picture of the wrong
    // place: the save re-grabs when the two no longer agree.
    const [addFrameSeconds, setAddFrameSeconds] = useState<number | null>(null);
    const [preparingAdd, setPreparingAdd] = useState(false);

    // A video-analysis run has no ocr.json, so the starting point for the review IS the
    // analysis output. Deriving it here (rather than making the reviewer press "Import AI
    // detections" on an empty list) is what makes a finished analysis behave like a finished
    // Inspection Insights run: the results are simply on screen.
    const aiDetections = useMemo(
      () => analysisToReviewedDetections(buildAnalysisDetections(videoAnalysis)),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [
        videoAnalysis.allPredictions,
        videoAnalysis.hasRoiData,
        videoAnalysis.roiDetections,
        videoAnalysis.analysisConfidenceOption,
      ]
    );

    // "Analysis output" shows the model result as it stands; anything else shows the APPROVED
    // rows only — with nothing approved that list is empty, which is the point: an unreviewed
    // analysis has no findings yet. Editing is allowed on the APPROVED view only — the model's
    // own output is read-only, so the two always stay comparable.
    const showingMachineOutput = source === "original";
    const working = showingMachineOutput ? aiDetections : approved.rows;
    const busy = approved.saving;

    // The analysis JSON's time -> metre track, so a row added or re-timed gets its station
    // from the video position instead of being looked up on the counter by hand.
    const track = useMemo(
      () => distanceTrack(videoAnalysis),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [videoAnalysis.vaData]
    );
    const distanceForTime = (seconds: number) => interpolatedDistanceAtTime(track, seconds);

    // Which coded fields on each row a human changed, measured against the analysis output --
    // the same comparison the Inspection Insights list makes against ocr.json, so a corrected
    // code is marked identically on both tabs. Built once per render, and only on the approved
    // view: the model's own output has nothing to be compared against.
    const machineByKey = useMemo(
      () => codedByKey(aiDetections as any[], (row) => codedFromReviewDto(row)),
      [aiDetections]
    );

    const changedFieldsFor = (row: ReviewedDetectionDto): Set<OcrCodedField> => {
      if (showingMachineOutput) return new Set();
      const coded = codedFromReviewDto(row);
      const machine = machineCounterpart(coded, row.stableKey, machineByKey);

      // No counterpart: the reviewer created this row outright, and its badge already says so.
      // Marking all nine fields as well states nothing per field and drowns out the marks on the
      // rows where a mark does mean something.
      if (!machine) return new Set();
      return changedCodedFields(coded, machine);
    };

    const statusLabel = (value?: string) =>
      t(
        `comparison.review.status.${
          (value ?? DEFAULT_DETECTION_STATUS) === "InReview"
            ? "in-review"
            : (value ?? DEFAULT_DETECTION_STATUS).toLowerCase()
        }`
      );

    const codeOptions = useMemo(() => {
      const codes = new Set<string>();
      (videoAnalysis as any).codesAvailableForDetection?.forEach?.((c: any) =>
        codes.add(typeof c === "string" ? c : c?.mainCode)
      );
      working.forEach((d) => d.mainCode && codes.add(d.mainCode));
      return Array.from(codes).filter(Boolean).sort();
    }, [videoAnalysis, working]);

    const toEditable = (d: ReviewedDetectionDto): Partial<EditableDetection> => ({
      main_Code: d.mainCode ?? "",
      distance: d.distance ?? 0,
      timestamp: d.timeSeconds !== undefined ? secondsToOcrTimestamp(d.timeSeconds) : "",
      c1: d.c1 ?? "",
      c2: d.c2 ?? "",
      q1: d.q1 ?? "",
      q2: d.q2 ?? "",
      sa: d.sa ?? "",
      postionFrom: d.positionFrom ?? "",
      positionTo: d.positionTo ?? "",
      frameUrl: (d as any).capturedFrame ?? d.frameUrl ?? "",
      text: d.note ?? "",
      detectionStatus: d.detectionStatus ?? DEFAULT_DETECTION_STATUS,
      conditionRating: d.conditionRating ?? "",
      riskRating: d.riskRating ?? "",
    });

    const fromEditable = (
      e: EditableDetection,
      previous?: ReviewedDetectionDto
    ): ReviewedDetectionDto => ({
      ...previous,
      source: previous?.source ?? "User",
      // An AI row the reviewer corrects becomes Edited; a row they created stays Added.
      //
      // Decided from whether the row EXISTED and was not the reviewer's own, rather than from
      // source being the exact string "AI". That test fell through to "Added" for any other
      // spelling -- so editing a model detection badged it "Added by reviewer" while the card
      // still showed the model's confidence next to it, which cannot both be true.
      decision:
        previous !== undefined && previous.source !== "User" && previous.decision !== "Added"
          ? "Edited"
          : "Added",
      mainCode: e.main_Code,
      distance: Number(e.distance) || 0,
      timeSeconds: e.timestamp ? ocrTsToSeconds(e.timestamp) : previous?.timeSeconds,
      c1: e.c1,
      c2: e.c2,
      q1: e.q1,
      q2: e.q2,
      sa: e.sa,
      positionFrom: e.postionFrom,
      positionTo: e.positionTo,
      // Only BYTES belong in capturedFrame. The dialog is seeded with whichever frame the row
      // has, which after a save is the stored image's link -- and echoing that back here read
      // as "a new capture", which cleared the stored file name server-side and orphaned the
      // picture. A link is display-only, so it is simply not sent.
      capturedFrame: isDataUrl(e.frameUrl)
        ? e.frameUrl
        : (previous as any)?.capturedFrame,
      // The observation text, which is what the XMLs, the JSON, the CSV and the HTML report all
      // carry as the description. Left empty, it is written from the coded fields -- the same
      // sentence a damage approved from the Inspection Graph gets, so a row added by hand is not
      // the one row in the document with no description.
      note: e.text.trim() || observationTextFromFields({
        mainCode: e.main_Code,
        c1: e.c1,
        c2: e.c2,
        q1: e.q1,
        q2: e.q2,
        sa: e.sa,
        positionFrom: e.postionFrom,
        positionTo: e.positionTo,
        distance: e.distance,
        timeSeconds: e.timestamp ? ocrTsToSeconds(e.timestamp) : undefined,
      }),
      detectionStatus: e.detectionStatus,
      // "" means the reviewer chose "not assessed"; send undefined so the field is simply
      // absent rather than an unparseable empty string.
      conditionRating: e.conditionRating || undefined,
      riskRating: e.riskRating || undefined,
    } as ReviewedDetectionDto);

    /**
     * Open Add with the frame at the current playhead already in it.
     *
     * The dialog shows a frame when it HAS one, and on Add nothing had ever taken one -- so the
     * reviewer was coding a damage with no picture of it on screen, which is the one thing they
     * need to see to code it at all. The station and the time already come from where the video
     * is paused; the frame comes from the same instant.
     *
     * Grabbed BEFORE the dialog opens rather than after. Patching it in afterwards would mean
     * changing `initial` while the dialog is up, and that resets every box the reviewer has
     * already filled in.
     */
    const openAdd = async () => {
      const seconds = videoPlayerService.videoProgress ?? 0;
      const initial: Partial<EditableDetection> = {
        timestamp: secondsToOcrTimestamp(seconds),
        // Empty, so the station is stated rather than defaulted — see DetectionList.
        distance: "",
        // Added by the reviewer, so it belongs to the approved list from the start.
        detectionStatus: "Approved",
      };

      setAddFrameSeconds(null);

      if (videoId !== undefined) {
        setPreparingAdd(true);
        try {
          const dataUrl = await (videoPlayerService as any).captureScreenshotAt(videoId, seconds);
          if (dataUrl) {
            initial.frameUrl = dataUrl;
            setAddFrameSeconds(seconds);
          }
        } catch (error) {
          // No picture is a poorer dialog, not a broken one: the save grabs one anyway.
          console.error("Frame capture failed:", error);
        } finally {
          setPreparingAdd(false);
        }
      }

      setAddInitial(initial);
      setAddOpen(true);
    };

    /**
     * Write a row the dialog just saved, then give it a frame if it has none.
     *
     * The frame matters as much as the coding -- it is what the ISYBAU Fotodatei points at, and
     * it is delivered with the documents -- and until now an added row could not get one: the
     * dialog has no camera, and the per-row camera button it used to rely on is gone. So the
     * frame is grabbed at the row's own position, exactly as approving a damage from the
     * Inspection Graph does. Capturing is not something a reviewer should have to remember.
     *
     * The row is written FIRST and the capture attached after, so it appears the moment the
     * dialog closes rather than a second later when the seek finishes. An existing picture is
     * never replaced: it is either the reviewer's own or one already stored for them.
     */
    const saveFromDialog = async (
      e: EditableDetection,
      previous?: ReviewedDetectionDto,
      // The position a frame carried INTO the dialog was taken at, or null when none was.
      seededAt: number | null = null
    ) => {
      const row = fromEditable(e, previous);
      approved.upsert(row, previous);

      // A seeded frame that no longer matches the row's time is worse than no frame: it shows a
      // stretch of pipe the observation is not about. Treated as missing, so it is replaced.
      const seedStale = seededAt !== null && row.timeSeconds !== seededAt;
      const hasFrame =
        (!seedStale && isDataUrl((row as any).capturedFrame)) || !!row.frameFileName;
      if (hasFrame || row.timeSeconds === undefined || videoId === undefined) return;

      try {
        const dataUrl = await (videoPlayerService as any).captureScreenshotAt(
          videoId,
          row.timeSeconds
        );
        // Matched on the row just written, whose station and main code are its identity now.
        if (dataUrl) {
          approved.upsert({ ...row, capturedFrame: dataUrl } as ReviewedDetectionDto, row);
        }
      } catch (error) {
        // The row stands either way; a frame that would not capture is not worth losing it over.
        console.error("Frame capture failed:", error);
      }
    };

    /**
     * Seed the list from what the model found, at the confidence currently selected. This is
     * the starting point for a review: the AI proposals, which the reviewer then corrects,
     * removes or adds to.
     */
    const importAiDetections = () => {
      // Re-sync against the model output: the reviewer changed the confidence, or re-ran the
      // analysis, and wants the newly surfaced detections. Rows they already have are kept —
      // only genuinely new (code, distance) pairs are appended, so this is not destructive.
      // Local, like every other change here: it lands on the server on Save.
      const base = approved.allRows;
      const known = new Set(
        base.map((d) => `${d.mainCode}|${Number(d.distance ?? 0).toFixed(2)}`)
      );
      const imported = aiDetections.filter(
        (d) => !known.has(`${d.mainCode}|${Number(d.distance ?? 0).toFixed(2)}`)
      );
      if (imported.length === 0) return;
      approved.replace([...base, ...imported]);
    };

    // The pending capture, else the stored frame the server minted a link for. Without the
    // second term a capture vanished from the card the moment it was saved: the save clears
    // capturedFrame and hands back frameUrl instead.
    //
    // Captures are made where a damage is approved -- the checkbox on the Analysis output
    // graph grabs the frame at that detection's timestamp. There is no per-row camera button
    // here any more: it did the same thing a second time, from the one view where the frame
    // had already been taken.
    const frameOf = (d: ReviewedDetectionDto): string | undefined =>
      (d as any).capturedFrame || d.frameUrl || undefined;

    return (
      <Box sx={{ width: "100%", p: 1 }}>
        {/* id="pdf-content-list-header": collected by OcrService.downloadPdf, the same anchor
            DetectionList puts on its own heading, so the exported cards arrive under a title
            instead of loose. Everything in here that is a CONTROL or a transient state is marked
            data-html2canvas-ignore -- a report is a record of the inspection, and a screenshot of
            four buttons and an "unsaved changes" chip is neither. */}
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, flexWrap: "wrap" }}
          id="pdf-content-list-header"
        >
          <Typography variant="h6">
            {showingMachineOutput
              ? t("comparison.va-detections.title-original")
              : t("comparison.va-detections.title")}
          </Typography>
          <Chip size="small" variant="outlined" label={working.length} />
          {/* Rows moved off Approved. Still stored — Approve all brings them back — but no
              longer part of the inspection. */}
          {!!approved.notApprovedCount && (
            <Typography variant="caption" color="text.secondary" data-html2canvas-ignore>
              {t("comparison.approved-list.not-approved")}: {approved.notApprovedCount}
            </Typography>
          )}
          {approved.dirty && (
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label={t("comparison.approved-list.unsaved")}
              data-html2canvas-ignore
            />
          )}

          <Box sx={{ flexGrow: 1 }} />

          {/* Write actions belong to the approved list only. The analysis output is the model's
              result, shown to be read — see DetectionList for the same rule on the OCR side. */}
          {/* Wrapped rather than marked one by one: a fragment cannot carry the attribute, and
              the flex/gap here keeps the row looking exactly as it did as four direct children. */}
          {!showingMachineOutput && (
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
              data-html2canvas-ignore
            >
              <Button size="small" variant="outlined" disabled={busy} onClick={importAiDetections}>
                {t("comparison.va-detections.import-ai")}
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={preparingAdd ? <CircularProgress size={14} /> : <Add />}
                disabled={busy || preparingAdd}
                onClick={() => void openAdd()}
              >
                {t("comparison.va-detections.add")}
              </Button>
              {/* Nothing above is written until this is pressed — see useApprovedList. */}
              <SaveListButton approved={approved} />
              <Button
                size="small"
                variant="text"
                disabled={!approved.dirty || busy}
                onClick={approved.discard}
              >
                {t("comparison.approved-list.discard")}
              </Button>
            </Box>
          )}
        </Box>

        {/* Whichever is true of the view you are on: this is the untouchable model output, or
            your edits are local until you save them. */}
        <Alert severity="info" sx={{ mb: 2 }} data-html2canvas-ignore>
          {t(
            showingMachineOutput
              ? "comparison.detection-source.read-only"
              : "comparison.detection-source.edits-go-to-approved"
          )}
        </Alert>

        {/* This side's sign-off. The OCR list being approved says nothing about this one. */}
        {review.isApprovedFor("Analysis") && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t("comparison.va-detections.approved-hint")}
          </Alert>
        )}

        {working.length === 0 && (
          <Alert severity="info">{t("comparison.va-detections.empty")}</Alert>
        )}

        {/* The same card the Inspection Insights list draws, from the same component: a reviewer
            moving between the two tabs is reading one inspection, and a different layout per tab
            made the same observation look like two different kinds of record. */}
        <Grid container spacing={2} sx={{ padding: 2 }}>
          {working.map((d, index) => {
            const changed = changedFieldsFor(d);
            const provenance = provenanceOf(d);

            return (
              <Grid item xs={12} key={`${d.stableKey ?? "new"}-${index}`}>
                <DetectionCard
                  badges={
                    <>
                      {!showingMachineOutput && provenance === "Added" && (
                        <Chip
                          size="small"
                          color="primary"
                          variant="outlined"
                          label={t("comparison.detection.added-by-reviewer")}
                        />
                      )}
                      {/* The model's own confidence. Kept although the OCR card has no
                          counterpart: it is the analysis side's reason to trust a row at all,
                          and an OCR row has no equivalent number to show. */}
                      {d.confidence !== undefined && d.confidence > 0 && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`${t("comparison.confidence")}: ${Math.round(
                            d.confidence * 100
                          )}%`}
                        />
                      )}
                      {/* The reviewer's verdict. All three always present on the approved view,
                          so an unset field reads "—" rather than vanishing — which would be
                          indistinguishable from "assessed as fine". */}
                      {!showingMachineOutput && (
                        <>
                          <Chip
                            size="small"
                            variant="outlined"
                            color={statusColor(d.detectionStatus ?? DEFAULT_DETECTION_STATUS)}
                            label={`${t("detection.assessment.status")}: ${statusLabel(
                              d.detectionStatus
                            )}`}
                          />
                          <Chip
                            size="small"
                            variant="outlined"
                            color={conditionColor(d.conditionRating)}
                            label={`${t("detection.assessment.condition")}: ${
                              d.conditionRating
                                ? t(`detection.condition.${d.conditionRating.toLowerCase()}`)
                                : "—"
                            }`}
                          />
                          <Chip
                            size="small"
                            variant="outlined"
                            color={riskColor(d.riskRating)}
                            label={`${t("detection.assessment.risk")}: ${
                              d.riskRating
                                ? t(`detection.risk.${d.riskRating.toLowerCase()}`)
                                : "—"
                            }`}
                          />
                        </>
                      )}
                    </>
                  }
                  actions={
                    showingMachineOutput ? undefined : (
                      <>
                        <Tooltip title={t("general.edit")}>
                          <IconButton
                            size="small"
                            aria-label="edit"
                            disabled={busy}
                            onClick={() => setEditIndex(index)}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("general.delete")}>
                          <IconButton
                            size="small"
                            aria-label="delete"
                            disabled={busy}
                            onClick={() => approved.remove(d)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )
                  }
                  // Analysis rows carry seconds into the video; OCR rows carry a clock timestamp.
                  // Converted here so both cards state the time the same way.
                  time={
                    d.timeSeconds === undefined
                      ? undefined
                      : formatDetectionTime(secondsToOcrTimestamp(d.timeSeconds))
                  }
                  distance={d.distance === undefined ? undefined : Number(d.distance).toFixed(2)}
                  mainCode={d.mainCode}
                  c1={d.c1}
                  c2={d.c2}
                  sa={d.sa}
                  q1={d.q1}
                  q2={d.q2}
                  positionFrom={d.positionFrom}
                  positionTo={d.positionTo}
                  text={d.note}
                  changed={changed}
                  frameUrl={frameOf(d)}
                  // The damage polygons, when "Mark damage" is on -- the same overlay the
                  // Inspection Insights cards draw, matched to this row's video position.
                  frameNode={
                    showSegmentation && frameOf(d) && d.timeSeconds !== undefined ? (
                      <ImageSegmentationOverlay
                        imageSrc={frameOf(d)!}
                        timestampSeconds={d.timeSeconds}
                        videoSegmentation={segmentation!}
                        filteredSegmentations={filteredSegmentations ?? []}
                      />
                    ) : undefined
                  }
                />
              </Grid>
            );
          })}
        </Grid>

        <DetectionEditDialog
          allowTextFromFields
          distanceForTime={distanceForTime}
          open={editIndex !== null}
          title={t("comparison.editDetection")}
          codeOptions={codeOptions}
          initial={editIndex !== null ? toEditable(working[editIndex]) : null}
          onCancel={() => setEditIndex(null)}
          onSave={(e) => {
            if (editIndex === null) return;
            // `previous` is passed so the row is matched on its ORIGINAL distance + main code —
            // an edit to either changes its identity. No seeded frame: an edited row shows
            // whatever picture it already had.
            void saveFromDialog(e, working[editIndex]);
            setEditIndex(null);
          }}
        />

        <DetectionEditDialog
          allowTextFromFields
          distanceForTime={distanceForTime}
          open={addOpen}
          title={t("comparison.addDetection")}
          codeOptions={codeOptions}
          initial={addInitial}
          onCancel={() => setAddOpen(false)}
          onSave={(e) => {
            void saveFromDialog(e, undefined, addFrameSeconds);
            setAddOpen(false);
          }}
        />
      </Box>
    );
  }
);

export default VaDetectionList;
